import CheckoutSettings from "./interfaces/checkoutSettings";
import CheckoutSummary from "./interfaces/checkoutSummary";
import PriceSummary from "./interfaces/priceSummary";
import ModalVisibility from "./interfaces/modalVisibility";
import WhitelabelSettings from "./interfaces/whitelabelSettings";
import Loading from "./interfaces/loading";
import carRentals from "./carRentals/index";
import rooms from "./rooms/index";
import service from "./services/index";
import flights from "./flights/index";
import fares from "./fares/index";
import payment from "./payment/index";
import personalData from "./personalData/index";
import accUpgrade from "./accUpgrade/index";
import inquiry, { BookingStatus } from "./inquiry/index";
import mutations from './mutations';
import actions from './actions';
import getters from './getters';
import PreInquiry from "./interfaces/preInquiry";
import { FARE_DEFAULT } from "@constants/fares";
import { CHECKOUT_DEFAULT_LOADING_TEXT } from "@constants/loading";
import { BOOKING_STEPS } from "@constants/steps";
import { AssistComments } from "@store/checkout/interfaces/assistComments";
import PaymentOptions from "@store/checkout/interfaces/paymentOptions";
import countries from "./countries/index";
import SiteSettings from "@store/checkout/interfaces/siteSettings";
import { CHECKOUT_API } from "@constants/api";

export interface CheckoutState {
    navigation: string[],
    activeIndex: number,
    activeNavigation: string,
    internalApiEndpoint: string,
    apiEndpoint: string,
    apiToken: string,
    checkoutSettings: CheckoutSettings,
    initialCheckoutSettings: CheckoutSettings,
    whitelabelSettings: WhitelabelSettings,
    isValid: boolean,
    isFinished: boolean,
    isWhitelabel: boolean,
    checkoutSummary: CheckoutSummary,
    priceSummary: PriceSummary,
    visibility: ModalVisibility,
    checkout: string,
    agentImage: string,
    preInquiry: PreInquiry,
    disableSubmitButton: boolean,
    fare: string,
    hasPayment: boolean,
    loading: Loading,
    isBottomSheetOpen: boolean,
    assistComments: AssistComments,
    sessionIdError: boolean,
    isOnlyNonBinding: boolean,
    paymentOptions: PaymentOptions,
    siteSettings: SiteSettings
}

export const possibleNavigationSteps: string[] = [
    BOOKING_STEPS.appointment,
    BOOKING_STEPS.fares,
    BOOKING_STEPS.flights,
    BOOKING_STEPS.rooms,
    BOOKING_STEPS.accommodation,
    BOOKING_STEPS.services,
    BOOKING_STEPS.carRental,
    BOOKING_STEPS.personalData,
    BOOKING_STEPS.overview,
    BOOKING_STEPS.payment
]

export const initialState: CheckoutState = {
    navigation: possibleNavigationSteps,
    activeIndex: 1,
    activeNavigation: '',
    internalApiEndpoint: CHECKOUT_API,
    apiEndpoint: '',
    apiToken: '',
    isWhitelabel: false,
    checkoutSettings: {
        sessionId: '',
        departureAirport: '',
        appointmentHash: '',
        organizerTitle: '',
        commissionModel: '',
        onlyNonBinding: false,
        hsCompanyId: 0,
        stageIdent: '',
        agb: '',
        legalAgreement: '',
        dataProtection: '',
        dataPolicyAgreement: '',
        bindingMode: '',
        restoreId: '',
        locale: '',
        trip: {
            id : 0,
            title: '',
            type: '',
            cancellationTitle: '',
            cancellationText: '',
            productUrl: '',
            imageUrl: ''
        }
    },
    initialCheckoutSettings: {
        sessionId: '',
        departureAirport: '',
        appointmentHash: '',
        organizerTitle: '',
        commissionModel: '',
        onlyNonBinding: false,
        hsCompanyId: 0,
        stageIdent: '',
        agb: '',
        legalAgreement: '',
        dataProtection: '',
        dataPolicyAgreement: '',
        bindingMode: '',
        restoreId: '',
        locale: '',
        trip: {
            id : 0,
            title: '',
            type: '',
            cancellationTitle: '',
            cancellationText: '',
            productUrl: '',
            imageUrl: ''
        }
    },
    whitelabelSettings: {
        consent: {
            label: '',
            required: false
        },
        additionalField: {
            name: '',
            text: '',
            required: false
        },
        agency: false,
        isIframe: false
    },
    isValid: false,
    isFinished: false,
    checkoutSummary: {
        tripId: 0,
        appointmentId: '',
        hsCompanyId: 0,
        rooms: [],
        hotelUpgrades: [],
        services: [],
        carRentals: [],
        travellerCount: {
            adults: 2,
        },
        tracking: {
            stageIdent: '',
            gtm: {
                sessionId: '',
                userId: '',
                clientId: ''
            },
            awin: {
                awaid: '',
                awbid: '',
                awgid: '',
                awpid: '',
                awchecksum: '',
                awclick: '',
                awcr: ''
            },
            travelagency: '',
            travelagencyExternal: '',
            agent: ''
        },
        fare: FARE_DEFAULT,
        sessionId: '',
        voucher: ''
    },
    priceSummary: {
        dateFrom: '',
        dateTo: '',
        airportFrom: '',
        costItems: [],
        totalPrice: 0,
        originalPrice: 0,
        onSitePrice: null,
        fare: FARE_DEFAULT,
        currency: '',
        voucherAmount: null,
        openAmount: null
    },
    visibility: {
        saveAndContinue: {
            isVisible: false
        },
        flightHistory: {
            content: {
                flight:
                    {
                        outbound: [],
                        outboundDuration: 0,
                        outboundStopCount: 0,
                        return: [],
                        returnDuration: 0,
                        returnStopCount: 0,
                        stopoverFlights: []
                    },
                id: '',
                surcharge: 0,
                note: '',
                title: '',
                type: '',
                airline: '',
                airlineLogoUrl: '',
                appointmentFlightId: ''
            },
            isVisible: false
        },
        error: {
            isVisible: false
        },
        accommodationUpgrade: {
            content: {
                amenitiesHotel: null,
                amenitiesRoom: null,
                board: null,
                calculationStatus: '',
                classification: 0,
                companyName: '',
                description: '',
                detailedDescription: null,
                id: '',
                imageUrls: [],
                nights: 0,
                price: 0,
                roomCategory: null,
                surcharge: 0,
                surchargePerPerson: 0,
                usp: null,
                location: null,
                day: '',
                parking: null,
                rating: '',
                ratingAmount: '',
                ratingOverall: '',
                ratingLink: ''
            },
            isVisible: false
        },
        carRentalUpgrade: {
            content: {},
            isVisible: false
        },
        restoreInfo: {
            isVisible: false
        },
        paxConnect: {
            isVisible: false,
            importUri: ''
        },
        booking: {
            isVisible: false
        },
        reload: {
            isVisible: false
        },
        personalData: {
            isVisible: false
        }
    },
    checkout: '',
    agentImage: '',
    preInquiry: {
        bookingRequest: {
            tripId: 0,
            appointmentId: '',
            travellerCount: {
                adults: 2,
            },
            rooms: [],
            hotelUpgrades: [],
            services: [],
            carRentals: [],
            tracking: {
                stageIdent: '',
                gtm: {
                    sessionId: '',
                    userId: '',
                    clientId: ''
                },
                awin: {
                    awaid: '',
                    awbid: '',
                    awgid: '',
                    awpid: '',
                    awchecksum: '',
                    awclick: '',
                    awcr: ''
                },
                travelagency: '',
                travelagencyExternal: '',
                agent: ''
            },
            fare: FARE_DEFAULT,
            sessionId: '',
            voucher: ''
        },
        bookingStep: BOOKING_STEPS.appointment,
        message: '',
        status: BookingStatus.NONE,
        sessionId: ''
    },
    disableSubmitButton: false,
    fare: FARE_DEFAULT,
    hasPayment: false,
    loading: {
        isActive: false,
        loadingTexts: CHECKOUT_DEFAULT_LOADING_TEXT
    },
    isBottomSheetOpen: false,
    assistComments: {
        appointmentComment: '',
        faresComment: '',
        flightsComment: '',
        roomsComment: '',
        accommodationComment: '',
        servicesComment: '',
        carRentalComment: '',
        personalDataComment: ''
    },
    sessionIdError: false,
    isOnlyNonBinding: false,
    paymentOptions: {
        googlePay: false,
        applePay: false,
        creditCard: true,
        klarna: false,
        iDeal: false,
        adyen: false,
        amex: false
    },
    siteSettings: {
        baseUrl: '',
        basePath: '/de/',
        pageFinish: '',
        pageError: '',
        pagePending: ''
    }
};

export default {
    namespaced: true,
    state: initialState,
    mutations,
    actions,
    getters,
    modules: {
        carRentals,
        rooms,
        service,
        accUpgrade,
        personalData,
        inquiry,
        flights,
        fares,
        payment,
        countries
    },
}
